import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { StackLayoutProps } from '@/components/layout/stack/types';
import { Button, SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';
import type { TypographyToken } from '@/theme/typography';
import React, { memo } from 'react';
import { Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type GuardianScreenHeaderProps = {
  title: string;
  onBack: () => void;
  /** Optional tablet-only right-side icon action (e.g. edit). Ignored on phone. */
  rightIcon?: AvailableIconName;
  onPressRight?: () => void;
  rightAccessibilityLabel?: string;
  /** Optional label rendered next to {@link rightIcon} (e.g. "Leave circle"). */
  rightLabel?: string;
  /** Tint for {@link rightIcon} and {@link rightLabel}. Defaults to the primary blue. */
  rightColor?: string;
  containerProps?: StackLayoutProps;
  /**
   * Phone-only title alignment. `'center'` optically centers the title by adding a
   * spacer opposite the back button. Defaults to `'left'`.
   */
  titleAlign?: 'left' | 'center';
  /** Override the phone title typography. Defaults to `Heading H6 18px`. */
  titleVariant?: TypographyToken;
};

function GuardianScreenHeader({
  title,
  onBack,
  rightIcon,
  onPressRight,
  rightAccessibilityLabel,
  rightLabel,
  rightColor,
  containerProps,
  titleAlign = 'left',
  titleVariant = 'Font Guardian App/Heading/Heading H6 18px',
}: GuardianScreenHeaderProps) {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();

  if (isTablet) {
    return (
      <Stack
        direction="row"
        backgroundColor={colors['0']}
        paddingTop={insets.top + scale(14)}
        paddingBottom={scale(14)}
        align="center"
        justify="space-between"
        paddingX={scale(24)}
        style={styles.tabletHeader}
        {...containerProps}>
        <Pressable
          accessibilityRole="button"
          onPress={onBack}
          style={({ pressed }) => [styles.tabletBackButton, pressed ? styles.pressed : null]}>
          <SvgIcon name="IcBackBlue" size={scale(24)} color={guardianColors['Main/Main']} />
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 SemiBold"
            style={styles.tabletBackButtonText}>
            Back
          </Text>
        </Pressable>
        <Text
          variant="Font Inter/Subtitle/Bold 14px"
          numberOfLines={1}
          style={styles.tabletHeaderTitle}>
          {title}
        </Text>
        {rightIcon ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={rightAccessibilityLabel}
            onPress={onPressRight}
            style={({ pressed }) => [styles.tabletRightButton, pressed ? styles.pressed : null]}>
            <SvgIcon
              name={rightIcon}
              size={scale(24)}
              color={rightColor ?? guardianColors['Main/Main']}
            />
            {rightLabel ? (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 SemiBold"
                style={{ color: rightColor ?? guardianColors['Main/Main'] }}>
                {rightLabel}
              </Text>
            ) : null}
          </Pressable>
        ) : (
          <Stack width={scale(24)} height={scale(24)} />
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      align="center"
      gap={scale(10)}
      backgroundColor={guardianColors['1']}
      paddingTop={insets.top + scale(2)}
      paddingX={scale(20)}
      paddingBottom={scale(16)}
      {...containerProps}>
      <Button variant="ghost" onPress={onBack} style={styles.backButton}>
        <Stack
          viewType="liquid-glass"
          align="center"
          justify="center"
          backgroundColor={withOpacity(colors['0'], 0.3)}
          overlayColor={withOpacity(colors['0'], 0.3)}
          intensity={20}
          tint="light"
          style={styles.backButtonGlass}>
          <Button.Icon
            name="IcBackBlue"
            color={guardianColors['10']}
            styleWrapper={styles.backButtonIcon}
          />
        </Stack>
      </Button>
      <Text
        variant={titleVariant}
        numberOfLines={1}
        style={titleAlign === 'center' ? styles.headerTitleCentered : styles.headerTitle}>
        {title}
      </Text>
      {onPressRight ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={rightAccessibilityLabel}
          onPress={onPressRight}
          style={({ pressed }) => [styles.phoneRightButton, pressed ? styles.pressed : null]}>
          {rightIcon ? (
            <SvgIcon
              name={rightIcon}
              size={scale(24)}
              color={rightColor ?? guardianColors['Main/Main']}
            />
          ) : null}
          {rightLabel ? (
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 SemiBold"
              style={{ color: rightColor ?? guardianColors['Main/Main'] }}>
              {rightLabel}
            </Text>
          ) : null}
        </Pressable>
      ) : titleAlign === 'center' ? (
        <Stack width={scale(40)} height={scale(40)} />
      ) : null}
    </Stack>
  );
}

export default memo(GuardianScreenHeader);

const useStyles = createStyles(({ scale }) => ({
  tabletHeader: {
    minHeight: scale(58),
    borderBottomWidth: 1,
    borderBottomColor: guardianColors['3'],
  },
  tabletBackButton: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(10),
    zIndex: 1,
  },
  tabletBackButtonText: {
    color: guardianColors['Main/Main'],
  },
  tabletHeaderTitle: {
    color: guardianColors['Main/Main'],
    position: 'absolute',
    left: scale(160),
    right: scale(160),
    bottom: scale(16),
    textAlign: 'center',
  },
  tabletRightButton: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
    zIndex: 1,
  },
  phoneRightButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(6),
  },
  headerTitle: {
    color: guardianColors['10'],
    flex: 1,
  },
  headerTitleCentered: {
    color: guardianColors['10'],
    flex: 1,
    textAlign: 'center',
  },
  pressed: {
    opacity: 0.72,
  },
  backButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    padding: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    shadowColor: 'transparent',
    elevation: 0,
  },
  backButtonGlass: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    paddingTop: scale(10),
    paddingRight: scale(12),
    paddingBottom: scale(10),
    paddingLeft: scale(10),
  },
  backButtonIcon: {
    marginRight: 0,
  },
}));
